import React from 'react';
import PropTypes from 'prop-types';
import { normalizeLanguage } from '../locales/index.js';
import { buildLanguageAlternateLinks } from '../utils/localeRouting.js';
import { writeLocalePreferenceCookie } from '../utils/localePreference.js';
import '../styles/LanguageAlternates.css';

const LABEL_BY_LANGUAGE = Object.freeze({
  en: 'This page in other languages',
  tr: 'Bu sayfa diğer dillerde',
  es: 'Esta página en otros idiomas',
  fr: 'Cette page dans d’autres langues',
  de: 'Diese Seite in anderen Sprachen',
  it: 'Questa pagina in altre lingue',
  id: 'Halaman ini dalam bahasa lain',
  th: 'หน้านี้ในภาษาอื่น',
  bn: 'এই পৃষ্ঠাটি অন্যান্য ভাষায়',
  hi: 'यह पेज अन्य भाषाओं में',
  pt: 'Esta página em outros idiomas'
});

// Following one of these links is an explicit language choice. Saving it before
// the browser navigates keeps the entry redirect and the client auto-switch from
// sending the visitor back. Pages without JavaScript rely on the Referer check in
// isLanguageAlternateNavigation instead.
const rememberLanguageChoice = (language) => {
  try {
    writeLocalePreferenceCookie(language);
    localStorage.setItem('ui_language', language);
  } catch {
    // noop
  }
};

// Crawlable links to the other language versions of the current page. Pass the
// exact hreflang entries the page renders in its <head>: the links are derived
// from them (minus x-default and the current language), so they always follow
// the published hreflang cluster, including any future locale pruning.
const LanguageAlternatesNav = ({ hrefLangEntries, currentLanguage, className = '' }) => {
  const links = buildLanguageAlternateLinks(hrefLangEntries, currentLanguage);
  if (!links.length) return null;

  const label = LABEL_BY_LANGUAGE[normalizeLanguage(currentLanguage)] || LABEL_BY_LANGUAGE.en;

  return (
    <nav className={`language-alternates ${className}`.trim()} aria-label={label}>
      <span className="language-alternates-label" aria-hidden="true">{label}</span>
      <ul>
        {links.map((link) => (
          <li key={link.language}>
            <a
              href={link.href}
              hrefLang={link.hrefLang}
              lang={link.language}
              onClick={(event) => {
                // A modified click opens another tab to compare languages; it
                // must not switch the saved language of this and other tabs.
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                rememberLanguageChoice(link.language);
              }}
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

LanguageAlternatesNav.propTypes = {
  hrefLangEntries: PropTypes.arrayOf(PropTypes.shape({
    hrefLang: PropTypes.string,
    href: PropTypes.string
  })).isRequired,
  currentLanguage: PropTypes.string,
  className: PropTypes.string
};

export default LanguageAlternatesNav;
